import {
    ErrorResponse,
    GetGoCancelsRequest,
    GetTripsReportRequest,
} from "../../../api";
import { GoCancelsList } from "../../../api/models/GoCancelsList";
import { TripsReportsRow } from "../../../api/models/TripsReportsRow";
import { TripsRequest } from "../../../api/models/TripsRequest";
import {
    TableSortState,
    TripsState,
    initTableSortState,
    tripsSlice,
} from "../../../features/ShiftMonitoring/store";
import {
    ActionType,
    ResourceType,
    StatusType,
    sendLog,
} from "../../../services/common/send_log";
import { commonSlice, store } from "../../../store";
import { getDriverById } from "../../common/drivers";
import { api } from "../../common/make_api";
import { Logout } from "../../login/login";

export const selectTripRecord = (
    trip: TripsReportsRow | null | undefined
): void => {
    const _trip = trip ? trip : null;
    store.dispatch(tripsSlice.actions.update_selected_trip(_trip));
};

export async function resetGoCancelPositions(): Promise<any> {
    store.dispatch(tripsSlice.actions.update_go_cancels(null));
}

export async function getGoCancelPositions(
    loginSessionId: string | null,
    unladenTripSessionId: string | null
): Promise<any> {
    store.dispatch(commonSlice.actions.startLoading());
    if (!unladenTripSessionId || !loginSessionId) {
        return;
    }
    try {
        const state = store.getState();
        const officeId = state.common.selectedOfficeId;

        const req_cancels: GetGoCancelsRequest = {
            goCancelRequest: {
                officeId,
                loginSessionId,
                unladenTripSessionId,
            },
        };

        const res_cancels: GoCancelsList = await api
            .getShiftReportsAPI()
            .getGoCancels(req_cancels);

        const go_cancels = res_cancels.goCancels ? res_cancels.goCancels : [];

        if (go_cancels.length == 0) {
            console.debug("There were no go cancels for this trip.");
        }

        store.dispatch(tripsSlice.actions.update_go_cancels(go_cancels));
        return go_cancels;
    } finally {
        store.dispatch(commonSlice.actions.finishLoading());
    }
}

export async function resetTripReports(): Promise<any> {
    store.dispatch(tripsSlice.actions.update_trips(null));
}

export const updateSortState = async (
    col: string,
    direction: string
): Promise<void> => {
    store.dispatch(
        tripsSlice.actions.update_table_sort({
            lastSortedColumn: col,
            sortDirection: direction,
        } as TableSortState)
    );
};

const resetTripState = () => {
    const newTripSelection: TripsState = {
        driverId: -1000,
        driverCode: "",
        driverName: "",
        shiftDate: "",
        shiftMin: "",
        naviUsageRate: -1,
        tableSortState: initTableSortState("trips"),
        needGuidanceHermesHeader: "",
    };

    store.dispatch(tripsSlice.actions.update_selection_state(newTripSelection));
};

export async function selectTripReports(
    shiftDate: string,
    officeId: number,
    driverId: number,
    needGuidanceHeader?: string,
    driverCode?: string,
    naviUsageRate?: number
): Promise<any> {
    store.dispatch(commonSlice.actions.startLoading());
    try {
        const state = store.getState();
        const _date = parseInt(shiftDate.split("-").join(""), 10);
        resetTripState();

        const sessionOfficeIds = state.common.officeIds;
        const officeids = sessionOfficeIds ? sessionOfficeIds : undefined;

        const tripsRequest: TripsRequest = {
            officeIds: officeids,
            officeId: officeId,
            driverId: driverId,
            shiftDate: _date,
        };

        const req_trips: GetTripsReportRequest = {
            tripsRequest: tripsRequest,
        };

        const res_trips = await api
            .getShiftReportsAPI()
            .getTripsReport(req_trips);

        sendLog(
            ResourceType.SHIFTS_MONITORING,
            StatusType.GET_RESOURCE,
            ActionType.SELECT_TRIPS_REPORTS,
            {
                action_type: ActionType.SELECT_TRIPS_REPORTS.toString(),
                selected_date: shiftDate,
                driver_id: driverId,
                driver_code: driverCode,
                office_id: officeId,
            }
        );

        if (res_trips.trips && res_trips.trips[0]) {
            const rec: TripsReportsRow = res_trips.trips[0];

            let naviUsage = -1;
            // if null or undefined or other falsy value, leave as -1; 0 is only if rate actually equals 0
            if (naviUsageRate == 0) {
                naviUsage = 0;
            } else {
                if (naviUsageRate) {
                    naviUsage = naviUsageRate;
                }
            }

            const elmt = getDriverById(rec.driverId, rec.officeId);

            const newTripSelection: TripsState = {
                driverId: rec.driverId ? rec.driverId : -1000,
                driverCode: driverCode ? driverCode : "-",
                driverName: elmt ? elmt.driverName : "",
                shiftDate: rec.shiftDate ? rec.shiftDate.toString() : "111",
                shiftMin: rec.shiftLenMin ? rec.shiftLenMin.toString() : "111",
                naviUsageRate: naviUsage,
                tableSortState: initTableSortState("trips"),
                needGuidanceHermesHeader: needGuidanceHeader
                    ? needGuidanceHeader
                    : "",
            };

            store.dispatch(
                tripsSlice.actions.update_selection_state(newTripSelection)
            );
        }

        store.dispatch(commonSlice.actions.updateSelectedOffice(officeId));

        store.dispatch(tripsSlice.actions.update_trips(res_trips.trips));

        return res_trips;
    } catch (err) {
        console.log("Got this error from trip_reports:", err);
        if (err["status"] == 401) {
            console.log("Session has expired");
            Logout();
            return;
        }

        if (err.message) {
            store.dispatch(
                commonSlice.actions.setErrorMessage([err.message as string])
            );
            await store.dispatch(commonSlice.actions.showServerErrorMessage());

            return;
        }
        if (err.status && err.status == 500) {
            await store.dispatch(commonSlice.actions.showServerErrorMessage());
            return;
        }
        const res = (await (err as Response).json()) as ErrorResponse;
        store.dispatch(commonSlice.actions.setErrorMessage([res.message!]));
    } finally {
        store.dispatch(commonSlice.actions.finishLoading());
    }
}
